import { useEffect, useState } from 'react';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { Tenant } from '@core/Entities/App/Tenants.entity';
import { Locale } from '@core/Entities/Locale/Locale.entity';
import { SimpleAggregation } from '@core/Entities/Search/Aggregations/Aggregations.entity';
import { FilterKey } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { Typography } from '@gds/Typography/Typography';
import { fetchAggregationsWithActiveFilters } from '@search/Utils/FetchAggregationsWithActiveFilters';
import { useAtomValue } from 'jotai';

import Link from 'next/link';

import { useFilterLink } from 'Hooks/SearchFilters/UseFilterLink';

import styles from './QuickLinks.module.css';

type QuickLinksProps = {
  filterBaseId: string;
  locale: Locale;
  links: string[];
  strings: Record<string, string>;
  tenant?: Tenant;
};

export const QuickLinks = ({ filterBaseId, locale, links, strings, tenant }: QuickLinksProps) => {
  const filtersList = useAtomValue(filtersListAtom);
  const [optsWithStock, setOptsWithStock] = useState<SimpleAggregation>({});
  const filterId = `${filterBaseId}__gte` as FilterKey;
  const partnerFilterId = `${filterBaseId}__lte` as FilterKey;

  const { getLinkForFilterValue } = useFilterLink({
    filterId,
    partnerFilterId,
    locale,
    isPlpFiltersSeoEnabled: true,
    tenant,
  });

  useEffect(() => {
    const fetchAggregations = async () => {
      const otherFilters = filtersList.filter(filter => !filter.id.startsWith(filterBaseId));
      const aggs = await fetchAggregationsWithActiveFilters(
        locale,
        otherFilters,
        undefined,
        tenant,
      );
      setOptsWithStock((aggs[filterBaseId as keyof typeof aggs] as SimpleAggregation) ?? {});
    };

    fetchAggregations();
  }, [filtersList, filterBaseId, locale]);

  return (
    <section className={styles.wrpr} data-test-id={`quick-links-${filterBaseId}`}>
      <Typography className={styles.title} variant="subheading3">
        {strings.quickLinks}
      </Typography>
      <div className={styles.linksCont}>
        {links.map((option: string) => {
          const link = getLinkForFilterValue({
            filterValue: option,
            resultCount: optsWithStock[option] ?? 0,
          });

          return link?.href ? (
            <Link key={option} href={link.href} rel={link.nofollow ? 'nofollow' : undefined}>
              {option}
            </Link>
          ) : (
            <span key={option}>{option}</span>
          );
        })}
      </div>
    </section>
  );
};
